import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, X } from 'lucide-react';
import { useAuth } from '../../../../auth/Auth';
import { createSocialSubmission, getSocialSubmissions, updateSocialSubmission } from '../../../../auth/_requests';
import type {
  CreateSocialSubmissionRequest,
  SocialSubmission,
  SocialSubmissionListResponse,
} from '../../../../helpers/utils/types';
import { formatDateToDisplayShortMonth } from '../../../../helpers/utils/utils';
import { AddSocialSubmissionModal } from '../components/AddSocialSubmissionModal';

const PAGE_SIZE = 10;
const EMPTY_VALUE = '-';

// Keep in sync with EDITABLE_SOCIAL_SUBMISSION_STATUSES in the node-api social-submission module
const EDITABLE_STATUSES: readonly SocialSubmission['status'][] = ['Pending', 'Change Request'];

const COLUMNS = [
  'Event',
  'Platform',
  'Username',
  'Post URL',
  'Status',
  'Created at',
  'Submitted at',
  'Reviewed at',
  'Reviewer comment',
  'Action',
] as const;

const formatDate = (raw?: string | null): string => {
  if (!raw || Number.isNaN(new Date(raw).getTime())) return EMPTY_VALUE;
  return formatDateToDisplayShortMonth(raw);
};

const SubmissionRow = ({
  submission,
  onEdit,
}: {
  submission: SocialSubmission;
  onEdit: (submission: SocialSubmission) => void;
}) => (
  <tr className='border-b border-black/5 last:border-b-0 hover:bg-[#f8fafc]'>
    <td className='px-4 py-3'>{submission.eventType}</td>
    <td className='px-4 py-3'>{submission.socialMediaType?.name ?? EMPTY_VALUE}</td>
    <td className='max-w-[220px] truncate px-4 py-3' title={submission.username}>{submission.username}</td>
    <td className='max-w-[220px] truncate px-4 py-3'>
      {submission.postUrl ? (
        <a
          href={submission.postUrl}
          target='_blank'
          rel='noopener noreferrer'
          className='text-[#2563eb] underline'
          title={submission.postUrl}
        >
          {submission.postUrl}
        </a>
      ) : (
        EMPTY_VALUE
      )}
    </td>
    <td className='px-4 py-3 font-semibold'>{submission.status}</td>
    <td className='whitespace-nowrap px-4 py-3'>{formatDate(submission.timestampAdded)}</td>
    <td className='whitespace-nowrap px-4 py-3'>{formatDate(submission.timestampSubmitted)}</td>
    <td className='whitespace-nowrap px-4 py-3'>{formatDate(submission.timestampReviewed)}</td>
    <td className='min-w-[200px] px-4 py-3'>{submission.reviewerComment || EMPTY_VALUE}</td>
    <td className='whitespace-nowrap px-4 py-3'>
      {EDITABLE_STATUSES.includes(submission.status) ? (
        <button
          type='button'
          className='font-semibold text-[#0055FF] underline'
          onClick={() => onEdit(submission)}
        >
          Edit
        </button>
      ) : (
        EMPTY_VALUE
      )}
    </td>
  </tr>
);

export const SocialSubmissionsSection = () => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const token = auth?.data?.token;

  const [page, setPage] = useState(1);
  const [submissions, setSubmissions] = useState<SocialSubmission[]>([]);
  const [meta, setMeta] = useState<SocialSubmissionListResponse['meta'] | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [submissionToEdit, setSubmissionToEdit] = useState<SocialSubmission | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  const loadSubmissions = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await getSocialSubmissions(token, { page, limit: PAGE_SIZE });
      setSubmissions(res.data ?? []);
      setMeta(res.meta ?? null);
    } catch (e) {
      setSubmissions([]);
      setMeta(null);
      setLoadError(e instanceof Error ? e.message : 'Could not load your social submissions.');
    } finally {
      setLoading(false);
    }
  }, [token, page]);

  useEffect(() => {
    void loadSubmissions();
  }, [loadSubmissions]);

  const closeModal = useCallback(() => {
    if (submitting) return;
    setModalOpen(false);
    setSubmissionToEdit(undefined);
    setSubmitError(null);
  }, [submitting]);

  const handleSubmit = async (body: CreateSocialSubmissionRequest) => {
    if (!token) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      if (submissionToEdit) {
        await updateSocialSubmission(token, submissionToEdit.socialSubmissionId, {
          username: body.username,
          postUrl: body.postUrl,
        });
      } else {
        await createSocialSubmission(token, body);
      }
      setModalOpen(false);
      setSubmissionToEdit(undefined);
      setConfirmationOpen(true);
      if (page === 1) await loadSubmissions();
      else setPage(1);
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'Could not submit your entry.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='en-panel is-active'>
      <div className='mb-4 flex flex-wrap items-center justify-between gap-3'>
        <div>
          <h3 className='text-[18px] font-semibold text-[#0A0B14]'>Social submissions</h3>
          <p className='text-[13px] text-[#64748b]'>
            Your Social Shout-Out and Social Follow entries and their review status.
          </p>
        </div>
        <button
          type='button'
          className='rounded-[12px] bg-[#0055FF] px-4 py-2.5 text-[14px] font-semibold text-white hover:opacity-95'
          onClick={() => {
            setSubmitError(null);
            setSubmissionToEdit(undefined);
            setModalOpen(true);
          }}
        >
          Add Submission
        </button>
      </div>

      <div className='overflow-x-auto rounded-[16px] border border-black/5 bg-white'>
        <table className='w-full min-w-[960px] text-left text-[13px] text-[#0A0B14]'>
          <thead className='bg-[#f8fafc]'>
            <tr>
              {COLUMNS.map((column) => (
                <th
                  key={column}
                  className='px-4 py-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#94a3b8]'
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={COLUMNS.length} className='px-4 py-8 text-center text-[#64748b]'>
                  Loading submissions…
                </td>
              </tr>
            ) : loadError ? (
              <tr>
                <td colSpan={COLUMNS.length} className='px-4 py-8 text-center text-red-600'>
                  {loadError}
                </td>
              </tr>
            ) : submissions.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length} className='px-4 py-8 text-center text-[#64748b]'>
                  No submissions yet. Add a submission to start earning.
                </td>
              </tr>
            ) : (
              submissions.map((submission) => (
                <SubmissionRow
                  key={submission.socialSubmissionId}
                  submission={submission}
                  onEdit={(item) => {
                    setSubmitError(null);
                    setSubmissionToEdit(item);
                    setModalOpen(true);
                  }}
                />
              ))
            )}
          </tbody>
        </table>

        {!loading && (meta?.lastPage ?? 1) > 1 && (
          <div className='en-ref-pagination'>
            <button
              type='button'
              className='en-ref-page-btn'
              disabled={!meta?.hasPreviousPage}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <div className='en-ref-page-label'>
              Page {meta?.page ?? 1} of {meta?.lastPage ?? 1}
            </div>
            <button
              type='button'
              className='en-ref-page-btn'
              disabled={!meta?.hasNextPage}
              onClick={() => setPage((p) => Math.min(meta?.lastPage ?? p, p + 1))}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {modalOpen && (
        <AddSocialSubmissionModal
          submissionToEdit={submissionToEdit}
          submitting={submitting}
          errorText={submitError}
          onClose={closeModal}
          onErrorDismiss={() => setSubmitError(null)}
          onSubmit={handleSubmit}
        />
      )}

      {confirmationOpen && (
        <div
          className='fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40'
          role='presentation'
          onClick={() => setConfirmationOpen(false)}
        >
          <div
            role='dialog'
            aria-modal='true'
            aria-labelledby='social-submission-confirmation-title'
            className='relative w-full max-w-[420px] rounded-[20px] bg-[#F4F6FB] p-6 text-center shadow-[0_24px_80px_rgba(15,23,42,0.18)]'
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type='button'
              onClick={() => setConfirmationOpen(false)}
              className='absolute right-4 top-4 rounded-full p-1.5 text-[#697386] hover:bg-white/80 transition-colors'
              aria-label='Close'
            >
              <X size={20} />
            </button>
            <CheckCircle2 size={40} className='mx-auto text-[#16A36E]' aria-hidden />
            <h2
              id='social-submission-confirmation-title'
              className='mt-4 font-display text-[22px] font-semibold text-[#0A0B14]'
            >
              Thanks for your submission!
            </h2>
            <p className='mt-2 text-[14px] text-[#6B7285]'>
              Our team will review it within 1–5 business days. In the meantime, explore other ways
              to earn points and tickets.
            </p>
            <button
              type='button'
              className='mt-6 w-full rounded-[14px] bg-[#0055FF] px-5 py-3.5 text-[15px] font-semibold text-white hover:opacity-95'
              onClick={() => {
                setConfirmationOpen(false);
                navigate('/rewards');
              }}
            >
              Explore ways to earn
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
